/**
 * ADForm Zod Validation Schema
 * Extends ARForm schema with detailed hazard arrays, address, and actions validation
 */

import { z } from 'zod';
import {
  hazardSeveritySchema,
  assessmentPhotoSchema,
  areasInspectedSchema,
  constructionTypeSchema,
  occupancyTypeSchema,
  postingStatusSchema,
  detailedEvalTypeSchema,
  apiRequiredText,
} from '@/pages/AssessmentRapid/ARForm/ARForm.schema';

/** 7-level damage percentage schema for detailed assessments (matches PWA estimated_building_damage) */
const detailedDamagePercentageSchema = z.enum([
  'none', '0_1', '1_10', '10_30', '30_60', '60_100', '100',
]);

// ============================================================================
// Detailed Hazard Record Schema
// ============================================================================

/**
 * Individual hazard severity record (used in hazard arrays)
 * Severity is optional in the base schema; array-level refinement
 * enforces that non-OTHER items must have a severity selected.
 */
export const hazardSeverityRecordSchema = z.object({
  hazardType: z.string().min(1, 'Hazard type is required'),
  severity: hazardSeveritySchema.optional(),
  comments: z.string().optional(),
  otherDescription: z.string().optional(),
});

/**
 * Validates a detailed hazard array: every item except "OTHER"
 * must have a severity selected. Produces per-item errors on the severity path.
 */
const detailedHazardArraySchema = z
  .array(hazardSeverityRecordSchema)
  .superRefine((items, ctx) => {
    items.forEach((item, index) => {
      if (item.hazardType !== 'OTHER' && item.severity === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please select a severity',
          path: [index, 'severity'],
        });
      }
    });
  });

// ============================================================================
// Main ADForm Schema
// ============================================================================

/**
 * Complete ADForm validation schema
 * Extends ARForm fields with detailed assessment fields
 */
export const adFormSchema = z.object({
  // ============================================
  // Assessment Mode
  // ============================================
  assessment_mode: z.literal('detailed'),

  // ============================================
  // Metadata
  // ============================================
  id: z.string().optional(),
  // Deliberately not required -- see the matching note in ARForm.schema.ts.
  parcelId: z.string(),

  // ============================================
  // Section 1: Inspection (4 fields)
  // ============================================
  inspectorId: z.string().min(1, 'Inspector ID is required'),
  affiliation: z.string().optional(),
  inspectionDateTime: z.string().min(1, 'Inspection date/time is required'),
  areasInspected: areasInspectedSchema,

  // ============================================
  // Section 2: Address (extended from ARForm building)
  // ============================================
  // Address fields optional for PWA parity (matches ARForm building step), except County --
  // see the matching note in ARForm.schema.ts.
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  county: apiRequiredText('County is required'),
  state: z.string().optional(),
  zipcode: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  buildingName: z.string().optional(),
  contactPhone: z.string().optional(),

  // ============================================
  // Section 3: Building Description (extended)
  // ============================================
  typeOfConstruction: constructionTypeSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select a construction type' }),
  constructionTypeOther: z.string().optional(),
  primaryOccupancy: occupancyTypeSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select a primary occupancy' }),
  occupancyTypeOther: z.string().optional(),
  storiesAboveGround: z.number().int().min(0).optional(),
  storiesBelowGround: z.number().int().min(0).optional(),
  buildingFootprint: z.number().min(0).optional(),
  numberOfUnits: z.number().int().min(0).optional(),
  totalResidentialUnits: z.number().int().min(0).optional(),
  inhabitableResidentialUnits: z.number().int().min(0).optional(),

  // ============================================
  // Section 4: Evaluation (extended with detailed hazard arrays)
  // ============================================
  hazards: z.object({
    collapseOrOffFoundation: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    buildingOrStoryLeaning: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    rackingDamageWalls: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    chimneyParapetFallingHazard: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    groundSlopeMovement: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    otherDescription: z.string().optional(),
    otherSeverity: hazardSeveritySchema.optional(),
  }),
  overallHazardCollapseComments: z.string().optional(),
  overallHazardOtherComments: z.string().optional(),
  generalComments: z.string().optional(),
  estimatedBuildingDamage: detailedDamagePercentageSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select estimated building damage' }),
  comments: z.string().optional(),
  structuralHazards: detailedHazardArraySchema,
  nonStructuralHazards: detailedHazardArraySchema,
  geotechnicalHazards: detailedHazardArraySchema,

  // ============================================
  // Section 5: Posting (AD-specific: previous posting + no preselection)
  // ============================================
  postingStatus: postingStatusSchema,
  restrictionsText: z.string().optional(),
  previousPostingInspected: z.boolean().optional(),
  previousPostingRestricted: z.boolean().optional(),
  previousPostingUnsafe: z.boolean().optional(),
  previousInspectorId: z.string().optional(),
  previousPostingDate: z.string().optional(),
  detailedEvalRecommended: z.boolean(),
  detailedEvalTypes: z.array(detailedEvalTypeSchema).optional(),

  // ============================================
  // Section 6: Actions (NEW)
  // ============================================
  barricadesNeeded: z.boolean(),
  barricadeLocations: z.string().optional(),
  engineeringEvalRecommended: z.boolean(),
  engineeringEvalStructural: z.boolean(),
  engineeringEvalGeotechnical: z.boolean(),
  engineeringEvalOther: z.string().optional(),
  otherRecommendations: z.array(z.string()).optional(),
  otherRecommendationsSpecify: z.string().optional(),
  actionsComments: z.string().optional(),

  // ============================================
  // Section 7: Image (same as ARForm)
  // ============================================
  photos: z.array(assessmentPhotoSchema).optional(),
});

/**
 * Inferred TypeScript type from the schema
 */
export type ADFormSchemaType = z.infer<typeof adFormSchema>;
